/* eslint-disable no-underscore-dangle */
import React from 'react';

import { graphql, Link as GatsByLink, useStaticQuery } from 'gatsby';
import { FormattedMessage, useIntl } from 'react-intl';
// eslint-disable-next-line import/no-unresolved
import { FeatureToggle, useFeatureToggle } from '@printdeal/feature-management/react';
import { Grid, Heading, Link } from '@printdeal/ui-library';
import { useSegmentation } from '@printdeal/segmentation/react';
import { findNodeByLocale } from '../../helpers/translations';
import useSystemPages from '../../hooks/useSystemPages';
import RequestQuoteButton from './RequestQuoteButton';
import { getConfiguration } from '../../helpers/Configuration';
import ConversationLoopLink from '../customer-programs/conversation-loop/ConversationLoopLink';

const personalAdviceQuery = graphql`
  query {
    knowledgeCentrePage: allContentfulPageContainer(filter: { slug: { eq: "kenniscentrum" } }) {
      nodes {
        path
        node_locale
      }
    }
  }
`;

interface PersonalAdviceWidgetProp {
  isLoggedIn: boolean;
  isCompanyCustomer: boolean;
}

const PersonalAdviceWidget = ({ isLoggedIn, isCompanyCustomer }: PersonalAdviceWidgetProp) => {
  const intl = useIntl();
  const { locale } = intl;
  const { knowledgeCentrePage } = useStaticQuery(personalAdviceQuery);
  const [accountDashboard, login, helpCenterIndex] = useSystemPages('accountDashboard', 'login', 'helpCenterIndex');

  const knowledgeCentrePageForLocale:
  { path?: string } | undefined = findNodeByLocale(knowledgeCentrePage.nodes, locale);

  const QUOTE_REQUEST_URL = `${accountDashboard?.path}/quoting/request/new`;
  const { featureToggle: { isEnabled: isCreateQuoteHomepageToggleEnabled } } = useFeatureToggle('create_quote_in_home_page');
  const configuration = getConfiguration();
  const { data: segmentationData } = useSegmentation();
  const { isReseller } = segmentationData ?? { isReseller: false };

  return (
    <>
      <Heading variant="display" size="2" className="pd-mb-3">
        <FormattedMessage id="widget.personaladvice.title" defaultMessage="Personal Advice" />
      </Heading>
      <Grid className="pd-gap-4">
        {
          helpCenterIndex?.path ? (
            <Link
              as={GatsByLink}
              to={helpCenterIndex.path}
              variant="secondary"
              iconBefore="customer-service"
            >
              {helpCenterIndex.title}
            </Link>
          ) : (
            <>
              {
                knowledgeCentrePageForLocale?.path && (
                  <Link
                    as={GatsByLink}
                    to={knowledgeCentrePageForLocale.path}
                    variant="secondary"
                    iconBefore="light-bulb"
                  >
                    <FormattedMessage id="widget.personaladvice.knowledgecenter" defaultMessage="Knowledge Center" />
                  </Link>
                )
              }
            </>
          )
        }
      </Grid>
      {isReseller ? (
        <Grid className="pd-mt-4">
          <ConversationLoopLink />
        </Grid>
      ) : null}
      {configuration?.quote?.enabled && (isCreateQuoteHomepageToggleEnabled || isLoggedIn) ? (
        <FeatureToggle id="create_quote_for_business_customer">
          <FeatureToggle.Enabled>
            {isCompanyCustomer ? (
              <RequestQuoteButton
                isLoggedIn
                QUOTE_REQUEST_URL={QUOTE_REQUEST_URL}
                loginPath={login?.path || ''}
              />
            ) : null}
          </FeatureToggle.Enabled>
          <FeatureToggle.Disabled>
            <RequestQuoteButton
              isLoggedIn
              QUOTE_REQUEST_URL={QUOTE_REQUEST_URL}
              loginPath={login?.path || ''}
            />
          </FeatureToggle.Disabled>
        </FeatureToggle>
      ) : null}
    </>
  );
};

export default PersonalAdviceWidget;
